import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {ImageType} from "../enums/image-type-enum";
import {ActivatedRoute, Router} from "@angular/router";
import {UserCardUtil} from "../utilityClass/userCardUtil";
import {MatDialog, MatDialogConfig} from "@angular/material/dialog";
import {QrGenerateComponent} from "../qr-generate/qr-generate.component";
import {UserCardResponse} from "../interface/userCardResponse";
import {UserCardService} from "../service/user-card.service";
import {TeamNotificationService} from "../service/team-notification-service";
import {SubscriptionResponse} from "../interface/subscriptionResponse";
import {HttpErrorResponse} from "@angular/common/http";
import {MockupDataModel} from "../qr-copy-message/mockup-data.model";
import {
  TEAM_SUBSCRIPTION_EXPIRY_ERROR_ALERT,
  USER_CARD_DUPLICATION_MESSAGE
} from "../constants/global.constants";
import {SubscriptionService} from "../service/subscription.service";
import {SnackBarUtil} from "../qr-copy-message/snack-bar-util.service";
import {Team} from "../interface/team";
import {ConnectReservedQrComponent} from "../connect-reserved-qr/connect-reserved-qr.component";
import {DialogService} from "../service/dialog.service";
import {TranslateService} from "@ngx-translate/core";

@Component({
  selector: 'app-single-card-grid',
  templateUrl: './single-card-grid.component.html',
  styleUrls: ['./single-card-grid.component.scss', 'single-card-grid-media.component.scss']
})
export class SingleCardGridComponent implements OnInit {

  @Input() singleCard: UserCardResponse | any;
  @Input() isFavoriteMap: { [key: string]: boolean } = {};
  @Input() isStarActive: boolean = false;
  @Input() isDeleteButtonFromHistoryActive: boolean = false;
  @Input() isDeleteButtonFromTeamActive: boolean = false;
  @Input() isDeleteButtonFromIndividualsActive: boolean = false;
  @Input() showEditSection: boolean = false;
  @Input() isCardActive: boolean = false;
  @Input() isIndividualCards: boolean = false;
  @Input() isTeamCards: boolean = false;
  @Input() activeSubscription!: SubscriptionResponse
  @Input() team!: Team
  @Input() reservedIdentificationId: string | null = null;

  @Output() deleteCardFromHistory: EventEmitter<void> = new EventEmitter();
  @Output() deleteCardFromIndividuals: EventEmitter<bigint> = new EventEmitter();
  @Output() toggleCardFromIndividuals: EventEmitter<bigint> = new EventEmitter();
  @Output() toggleCardFromTeam: EventEmitter<bigint> = new EventEmitter();
  @Output() cardAdded = new EventEmitter<UserCardResponse>();
  @Output() activeReservedQrCard: EventEmitter<string> = new EventEmitter();

  protected readonly ImageType = ImageType;

  clicked: boolean = false;
  companyLogoExists: boolean = true;
  currentRoute: string | null = null;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private userCardUtil: UserCardUtil,
    private userCardService: UserCardService,
    private dialog: MatDialog,
    private teamNotificationService: TeamNotificationService,
    private subscriptionService: SubscriptionService,
    private snackBarUtil: SnackBarUtil,
    private dialogService: DialogService,
    private translateService: TranslateService
  ) {
  }

  ngOnInit() {
    this.currentRoute = this.activatedRoute.snapshot.url.join('/');
    const imageUrl = this.getUserCardImage(this.singleCard.userCardResponse.id, ImageType.COMPANY_LOGO);
    this.userCardService.checkImageExists(imageUrl).subscribe(exists => {
      this.companyLogoExists = exists;
    });
  }

  onDeleteFromHistory(id: bigint, event: any) {
    event.stopPropagation();
    this.deleteCardFromHistory.emit(event);
  }

  onDeleteFromTeam(event: any) {
    event.stopPropagation();
    this.teamNotificationService.notifyDelete(this.singleCard.userCardResponse.cardId);
  }

  onDeleteFromIndividuals(id: bigint, event: any) {
    event.stopPropagation();
    this.deleteCardFromIndividuals.emit(id);
  }

  onStar(cardId: bigint, event: any) {
    if (this.isCardInactive()) {
      return;
    }
    event.stopPropagation();
    this.userCardUtil.toggleFavorite(cardId, event, this.isFavoriteMap)
  }

  toggle(cardId: string, reservedId: string, event: Event): void {
    event.stopPropagation();
    this.activeReservedQrCard.emit(cardId)
    this.openPopup(cardId, reservedId)
  }

  openPopup(cardId: string, reservedId: string): void {
    this.dialogService.openReservedQrPopup(
      ConnectReservedQrComponent,
      {cardId, reservedIdentificationId: reservedId},
      this.currentRoute!,
      () => {
        this.singleCard.userCardResponse.isReservedCardActive = false;
      }
    );
  }

  navigateToeDitPage(id: bigint) {
    this.router.navigate(['/cards/edit', id], {
      queryParams: {
        currentRoute: this.currentRoute,
      },
      state: {activeSubscription: this.activeSubscription}
    });
  }

  showQRCode(userCardId: bigint, event: any): void {
    event.stopPropagation();

    this.userCardService.generateQRCode(userCardId).subscribe((qrCodeBlob: Blob) => {
      const qrUrl = URL.createObjectURL(qrCodeBlob);
      const dialogConfig = new MatDialogConfig();

      dialogConfig.data = {
        userCard: this.singleCard.userCardResponse,
        qrCodeUrl: qrUrl
      };

      dialogConfig.panelClass = 'qr-dialog';

      this.dialog.open(QrGenerateComponent, dialogConfig);
    });
  }

  isCardInactive() {
    return (!this.isCardActive || this.singleCard.userCardResponse.subscriptionExpired);
  }

  truncateText(value: string, limit: number): string {
    if (value.length <= limit) {
      return value;
    }
    return value.substring(0, limit) + '...';
  }

  navigateToUserSinglePage(userCardId: string) {
    this.router.navigate(['/', userCardId]);
  }

  getUserCardImage(id: bigint, type: ImageType) {
    return this.userCardService.getCardImage(id, type);
  }

  toggleSwitch(event: any) {
    event.stopPropagation();
    this.isCardActive = !this.isCardActive;
    this.teamNotificationService.notifyChangeActivity(this.singleCard.userCardResponse.id);
  }

  toggleSwitchActiveOrInActiveCard(id: bigint, event: Event): void {
    event.stopPropagation();
    this.isCardActive = !this.isCardActive;

    if (this.isIndividualCards) {
      this.toggleCardFromIndividuals.emit(id);
    } else {
      this.toggleCardFromTeam.emit(id);
    }
  }

  openEditDialogForMemberAdd(): void {
    const teamOwnerId = this.team?.owner?.id;
    this.subscriptionService.checkTeamSubscription(teamOwnerId).subscribe({
      next: (isTeamSubscription) => {
        if (isTeamSubscription) {
          this.userCardService.duplicate(this.singleCard.userCardResponse.id).subscribe(res => {
            this.cardAdded.emit(res);
            const msg = this.translateService.instant('user_card_duplicated_success');
            this.showSnackbar(MockupDataModel.success(msg), SnackBarUtil.CLASSES.success)
          })
        }
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 409) {
          this.router.navigate(['/subscriptions'], {state: {isShowAllSubscriptions: true, isIndividualActive: false}});
          this.showSnackbar(MockupDataModel.error(this.translateService.instant('invite_limit_error')), SnackBarUtil.CLASSES.error)
        } else if (error.status === 404) {
          this.router.navigate(['/subscriptions'], {state: {isShowAllSubscriptions: true, isIndividualActive: false}});
          this.showSnackbar(MockupDataModel.error(TEAM_SUBSCRIPTION_EXPIRY_ERROR_ALERT), SnackBarUtil.CLASSES.error)
        }
      }
    });
  }

  private showSnackbar(data: MockupDataModel, panelClassNames: string[]): void {
    this.snackBarUtil.openSnackBar({
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: panelClassNames,
      data: data
    });
  }
}
